import LoadingButton from '@atlaskit/button/loading-button';
import Form, { Field, FormApi, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import { isEmpty } from 'lodash';
import get from 'lodash/get';
import set from 'lodash/set';
import React, { useState } from 'react';
import { UpdateOrderRequest, isPresent, AddOnOption } from '@atlassian/cssxp-typescommon';

import { AddOn, InvoiceGroup, Maybe, Statuspage } from '@css-xp/graphtypes';
import StatuspageAvailableAddOnsFormField from './statuspage-available-add-ons-form-field';
import { updateEntitlementPlan } from '@atlassian/temp-proxies';
import { InvoiceGroupFormField, InvoiceGroupSelectItem } from './invoice-group-form-field';

import { useDispatch } from 'react-redux';
import { ApolloQueryResult } from '@apollo/client';
import { useFormSubmit, useNotification } from '../../hooks';
import { orderSuccessNotification } from '../../notifications';
import { InfoPanel } from '../../info-panel';
import { ErrorMessageOnInfoPanel } from '../../error-page';
import { actions as SidebarActions } from '../../state';
import { TransactionAccountContextType } from '../../common-context';

interface FormData {
  productId?: string | null;
  invoiceGroupId?: InvoiceGroupSelectItem;
  selectedAddOns?:
    | {
        selectedAddOn: { label: string; value: { offeringId: string } };
        quantity: string;
      }[]
    | undefined;
}
interface Props {
  entitlement: Statuspage;
  availableAddOns: AddOn[];
  hasPaymentMethod: boolean;
  transactionAccountContext: TransactionAccountContextType;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}

export const SubscribeToAddOnsForm = ({ entitlement, availableAddOns, refetch, transactionAccountContext }: Props) => {
  const dispatch = useDispatch();

  const [selectedAddOns, setSelectedAddOns] = useState<AddOnOption[]>([{}]);

  const { error, isProcessing, submitForm, complete, data } = useFormSubmit<UpdateOrderRequest, any>({
    submitFormAction: updateEntitlementPlan,
    onComplete: () => {
      dispatch(SidebarActions.closeSidebar());
      refetch();
    },
  });

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const { getTransactionAccount, data: transactionAccountData } = transactionAccountContext;

  const availableAddOnsFormOptions: AddOnOption[] =
    availableAddOns?.filter(removeEmptyAddOns).map(formatAddonsForFormFields).filter(removeAddOnsWithEmptyFields) ?? [];

  const removeAddOn = (indexToRemove: number) => {
    setSelectedAddOns((addOns) => addOns.filter((_addOn, index) => indexToRemove !== index));
  };

  const addAdditionalAddOn = () => {
    setSelectedAddOns((addOns) => [...addOns, {}]);
  };

  const handleSubmit = (addOnData: FormData, formApi: FormApi<FormData>) => {
    // @atlaskit/form does not handle default form values well, creating thing like `selectedAddOns[0].selectedAddOn` instead of creating an object at that index. Lodash has our back.
    const transformedData: FormData = { selectedAddOns: [] };
    // setting key to any as getFieldState uses the actual keys of FormData, which does not include array indices
    formApi.getRegisteredFields().forEach((key: any) => {
      // get the value from the field state or the form data
      const value = formApi.getFieldState(key)?.value ?? get(addOnData, key);
      set(transformedData, key, value);
    });

    const formattedAddOnSubscriptionRequests = transformedData.selectedAddOns
      ?.map((addOn) => ({
        offeringId: addOn.selectedAddOn.value.offeringId,
        units: addOn.quantity,
      }))
      .filter(isPresent);

    const requestData: UpdateOrderRequest = {
      invoiceGroupId: get(transformedData, 'invoiceGroupId.value', ''),
      entitlementId: entitlement.id,
      addOns: formattedAddOnSubscriptionRequests,
    };

    submitForm(requestData);
  };

  if (complete && !notificationSent) {
    const transitionTimeError =
      data.transitionTime === 'unknown'
        ? ' - We were unable to determine if the successfully submitted order is immediate or scheduled.'
        : undefined;
    const notificationOptions = orderSuccessNotification(
      'SUBSCRIBE_TO_ADDONS',
      data.transitionTime,
      transitionTimeError
    );
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  const transactionAccount = getTransactionAccount(transactionAccountData);
  // Check if there is a payment method in the transaction account
  const hasPaymentMethod = transactionAccount?.availablePaymentMethods?.totalCount !== 0;

  const availableInvoiceGroups = (transactionAccount?.availableInvoiceGroups?.nodes ?? []) as Maybe<InvoiceGroup[]>;

  const defaultBillingGroupId = entitlement?.activeSubscriptionBillingGroupId ?? entitlement?.billingGroupId;

  return (
    <Form onSubmit={handleSubmit}>
      {({ formProps }) => (
        <form {...formProps}>
          <FormHeader title="Subscribe to add-ons"></FormHeader>
          <FormSection>
            <div
              style={{
                maxWidth: '500px',
              }}
            >
              {!hasPaymentMethod && (
                <InfoPanel
                  type="warning"
                  title="Cannot subscribe to add-ons - there are no payment methods on file for this entitlement"
                  message={''}
                />
              )}

              {error && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorMessageOnInfoPanel
                      title="We're unable to subscribe to add ons for the following reason"
                      message={error}
                    />
                  }
                />
              )}

              <Field name="productId" defaultValue={entitlement.entitlementNumber} label="Product ID">
                {({ fieldProps }) => (
                  <Textfield
                    style={{ maxWidth: '500px' }}
                    {...fieldProps}
                    isReadOnly
                    name="productId"
                    value={`${entitlement.entitlementNumber}`}
                  />
                )}
              </Field>
            </div>
            <table>
              <StatuspageAvailableAddOnsFormField
                availableAddOns={availableAddOnsFormOptions}
                selectedAddOns={selectedAddOns}
                removeAddOn={removeAddOn}
                addAdditionalAddOn={addAdditionalAddOn}
                layoutOptions={{ showLabels: true }}
              />
            </table>
          </FormSection>
          <FormSection>
            <InvoiceGroupFormField
              productId={entitlement?.productId ?? undefined}
              availableInvoiceGroups={availableInvoiceGroups}
              entitlementBillingInvoiceGroupId={defaultBillingGroupId}
              scheduledBillingInvoiceGroupId={entitlement?.scheduledChanges?.scheduledBillingInvoiceGroupId}
            />
          </FormSection>
          <FormFooter align="start">
            <LoadingButton appearance="primary" type="submit" isLoading={isProcessing} testId="submit-add-on">
              Subscribe to add-ons
            </LoadingButton>
          </FormFooter>
        </form>
      )}
    </Form>
  );
};

const removeEmptyAddOns = (addOn: AddOn): addOn is AddOn => !isEmpty(addOn);
const formatAddonsForFormFields = (addOn: AddOn) => ({
  name: addOn.name,
  offeringId: addOn.offeringId,
});
const removeAddOnsWithEmptyFields = (addOn: AddOn): addOn is AddOnOption =>
  !isEmpty(addOn.name) && !isEmpty(addOn.offeringId);
